/**
 * LE DÉPLIANT « VALEURS » (DEV-1508 lot 2, engagement E2, spec v1.5 §2).
 * ============================================================================
 *
 * INTENTION, mot pour mot de Mathieu le 09/09 : « Attention sur les tableaux
 * quand on descend à des pas très fins il y a trop de valeurs… Il faut le régler
 * intelligemment. » Un dépliant qui suivrait le graphe ligne pour ligne ferait
 * 4 032 lignes sur quinze jours de courbe 5 minutes. Personne ne lit cela.
 *
 * Trois règles, dans cet ordre.
 *
 * 1. LE GRAIN DU TABLEAU EST PLAFONNÉ PAR LE NOMBRE DE LIGNES, pas par le grain
 *    du graphe : le tableau prend le grain le plus fin qui tient dans 750 lignes
 *    sur la fenêtre regardée. Il ne descend jamais sous le grain servi (on ne
 *    fabrique pas des points qu'on n'a pas), et quand il est plus grossier que le
 *    graphe, l'écran le DIT et donne le geste pour descendre.
 *
 * 2. UN GRAIN AGRÉGÉ PORTE MOYENNE, MIN ET MAX, jamais une valeur seule : le pic
 *    reste lisible dans le tableau comme dans le graphe. Une grandeur qui
 *    s'additionne (énergie, durée) porte la somme.
 *
 * 3. UNE ABSENCE RESTE UNE ABSENCE. Un groupe sans aucun point mesuré rend des
 *    cellules VIDES, jamais des zéros : `null` traverse tout ce module intact
 *    (décision de Mathieu du 07/09). `n` dit combien de points ont été agrégés.
 *
 * Ce que le tableau n'est PAS : un export. Qui veut ses 4 032 lignes les prend
 * par le bouton Exporter, inchangé (E10).
 */
import { origineDUnGroupe } from "@modules/data/utils/origineEnedis"
import { finInclusiveDeLaPeriode } from "@modules/data/utils/graphesEnedis"
import type { OrigineSerie } from "@modules/data/utils/ongletsEnedis"

/** Grains que le tableau sait porter, du plus fin au plus grossier. */
export type GrainTableau = "natif" | "hourly" | "daily" | "weekly"

/** Au delà, on ne peint pas : on monte d'un grain. */
export const PLAFOND_LIGNES = 750

/** Pagination : vingt-quatre lignes, 48 et 96 au choix (spec v1.5 §2 règle 3). */
export const LIGNES_PAR_PAGE = 24
export const CHOIX_LIGNES_PAR_PAGE = [24, 48, 96]

const ORDRE_GRAINS: GrainTableau[] = ["natif", "hourly", "daily", "weekly"]

export const LIBELLES_GRAIN_TABLEAU: Record<GrainTableau, string> = {
    natif: "au pas de la courbe",
    hourly: "à l'heure",
    daily: "au jour",
    weekly: "à la semaine",
}

/**
 * Le grain d'une série servie AU GRAIN PÉRIODE n'est ni une heure ni un jour :
 * chaque ligne est une période de facturation, bornée par son « Du » et son
 * « Au ». Le titre le dit avec ses mots à elle (maquette v1.5, `tablePeriodes`).
 */
export const LIBELLE_GRAIN_PERIODE = "par période"

/**
 * LE GRAIN, ÉCRIT DANS LE TITRE (V1, maquette v1.5).
 *
 * Au pas natif, le titre écrit le PAS quand on le connaît (« au pas de 30 min »)
 * plutôt que la périphrase « au pas de la courbe » : c'est le fait que le client
 * cherche, et il est servi par le lecteur. Sans lui, la périphrase reste — on ne
 * devine pas un pas.
 */
export function libelleDuGrain(
    grain: GrainTableau,
    options: { periode?: boolean; minutesPasNatif?: number | null } = {}
): string {
    if (options.periode) return LIBELLE_GRAIN_PERIODE
    const minutes = options.minutesPasNatif
    if (grain === "natif" && typeof minutes === "number" && minutes > 0) {
        return minutes % 60 === 0 && minutes >= 60
            ? `au pas de ${minutes / 60} h`
            : `au pas de ${minutes} min`
    }
    return LIBELLES_GRAIN_TABLEAU[grain]
}

/**
 * LE TITRE DU DÉPLIANT, FERMÉ COMME OUVERT (V1, maquette v1.5 l.345-347).
 *
 * « Valeurs au jour, 92 lignes » : le grain RÉEL et le nombre de lignes RÉEL.
 * Fermé, c'est tout ce qu'on sait du dépliant ; le cacher jusqu'à l'ouverture
 * obligeait à ouvrir pour savoir si cela valait la peine d'ouvrir.
 *
 * Le suffixe « (moyenne, min, max) » n'est écrit que lorsque le tableau porte
 * réellement ces trois colonnes : promettre une statistique absente serait pire
 * que se taire.
 */
export function titreDuDepliant(params: {
    grain: GrainTableau
    lignes: number
    /** Le tableau porte moyenne, minimum ET maximum. */
    statistiques?: boolean
    periode?: boolean
    minutesPasNatif?: number | null
}): string {
    const grain = libelleDuGrain(params.grain, {
        periode: params.periode,
        minutesPasNatif: params.minutesPasNatif,
    })
    const nombre = params.lignes.toLocaleString("fr-FR")
    const mot = params.lignes === 1 ? "ligne" : "lignes"
    const stats = params.statistiques ? " (moyenne, min, max)" : ""
    return `Valeurs ${grain}, ${nombre} ${mot}${stats}`
}

/** `"10 min"` → 10. Rend `null` quand le pas n'est pas servi : rien ne se devine. */
export function minutesDuPas(pas: string | null | undefined): number | null {
    if (!pas) return null
    const m = /^(\d+)\s*min/.exec(String(pas).trim())
    if (!m) return null
    const minutes = Number(m[1])
    return minutes > 0 ? minutes : null
}

/** Nombre de lignes qu'un grain produirait sur une fenêtre de `jours` jours. */
export function lignesAttendues(
    grain: GrainTableau,
    jours: number,
    minutesPasNatif: number | null
): number | null {
    if (jours <= 0) return 0
    switch (grain) {
        case "natif":
            // Sans le pas de la courbe, on ne sait pas combien de lignes le natif
            // ferait : on ne le choisit pas au hasard, on passe au grain suivant.
            return minutesPasNatif
                ? Math.ceil((jours * 1440) / minutesPasNatif)
                : null
        case "hourly":
            return jours * 24
        case "daily":
            return jours
        case "weekly":
            return Math.ceil(jours / 7)
    }
}

/**
 * Le grain du tableau : le plus fin qui tient dans 750 lignes, jamais plus fin
 * que le grain réellement servi par le graphe.
 */
export function grainDuTableau(params: {
    jours: number
    /** Grain des séries servies : le tableau ne descend pas en dessous. */
    grainServi: GrainTableau
    minutesPasNatif?: number | null
}): GrainTableau {
    const depart = Math.max(0, ORDRE_GRAINS.indexOf(params.grainServi))
    for (const grain of ORDRE_GRAINS.slice(depart)) {
        const lignes = lignesAttendues(
            grain,
            params.jours,
            params.minutesPasNatif ?? null
        )
        if (lignes !== null && lignes <= PLAFOND_LIGNES) return grain
    }
    return "weekly"
}

/** Vrai quand le tableau est plus grossier que le graphe : l'écran doit le dire. */
export function tableauPlusGrossier(
    grainTableau: GrainTableau,
    grainServi: GrainTableau
): boolean {
    return ORDRE_GRAINS.indexOf(grainTableau) > ORDRE_GRAINS.indexOf(grainServi)
}

/**
 * La phrase servie quand le tableau est plus grossier que le graphe. Elle donne
 * le GESTE, elle ne s'excuse pas.
 */
export function phraseDuGrain(
    grainTableau: GrainTableau,
    grainServi: GrainTableau
): string | null {
    if (!tableauPlusGrossier(grainTableau, grainServi)) return null
    // Le geste dépend de ce qu'il faut atteindre : l'heure tient sur un ou deux
    // jours, le pas de la courbe demande de resserrer plus encore.
    const geste =
        grainTableau === "hourly" ? "sur un ou deux jours" : "sur un mois"
    return (
        `Le tableau est ${LIBELLES_GRAIN_TABLEAU[grainTableau]} pour rester ` +
        `lisible (${PLAFOND_LIGNES} lignes au plus) ; pour le pas plus fin, ` +
        `resserrez la fenêtre regardée ${geste}. Le graphe, lui, est ` +
        `${LIBELLES_GRAIN_TABLEAU[grainServi]}.`
    )
}

/**
 * COMBIEN DE LIGNES CE GRAIN FERAIT SUR CES POINTS-CI (V1, plafond des 750).
 *
 * `lignesAttendues` raisonne sur une DURÉE : elle dit ce qu'une fenêtre pleine
 * produirait. Le titre, lui, annonce des lignes qui existent, et une série
 * trouée en a moins. On compte donc les groupes réels, en un seul passage.
 */
export function nombreDeLignes(
    x: Array<string | [string, string]>,
    grain: GrainTableau
): number {
    const vues = new Set<string>()
    for (const point of x) {
        const iso = Array.isArray(point) ? point[0] : point
        if (typeof iso !== "string" || iso === "") continue
        vues.add(cleDeGroupe(iso, grain))
    }
    return vues.size
}

/**
 * LE GRAIN QUI TIENT VRAIMENT DANS LE PLAFOND (V1 et §7.5 du plan).
 *
 * `grainDuTableau` décide sur la fenêtre REGARDÉE ; le dépliant, lui, chiffre
 * tout le CONTEXTE chargé. Un contexte de deux ans regardé sur un mois rendait
 * donc un tableau de 730 lignes annoncé « au jour » — et de 8 760 lignes le jour
 * où la fenêtre est horaire. On remonte d'un cran tant que le compte réel
 * dépasse le plafond, et le titre dit le grain auquel on s'est arrêté.
 */
export function grainQuiTient(
    x: Array<string | [string, string]>,
    grainDepart: GrainTableau
): GrainTableau {
    const depart = Math.max(0, ORDRE_GRAINS.indexOf(grainDepart))
    for (const grain of ORDRE_GRAINS.slice(depart)) {
        if (nombreDeLignes(x, grain) <= PLAFOND_LIGNES) return grain
    }
    return "weekly"
}

// ============================================================================
// LES LIGNES
// ============================================================================

/** Clé de groupement d'un horodate au grain du tableau. */
export function cleDeGroupe(x: string, grain: GrainTableau): string {
    switch (grain) {
        case "natif":
            return x
        case "hourly":
            return `${x.slice(0, 13)}:00`
        case "daily":
            return x.slice(0, 10)
        case "weekly": {
            const d = new Date(`${x.slice(0, 10)}T00:00:00Z`)
            if (Number.isNaN(d.getTime())) return x.slice(0, 10)
            // Lundi de la semaine, en temps universel.
            const decalage = (d.getUTCDay() + 6) % 7
            d.setUTCDate(d.getUTCDate() - decalage)
            return d.toISOString().slice(0, 10)
        }
    }
}

/** Une ligne du dépliant. Chaque cellule vaut `null` quand rien n'a été mesuré. */
export type LigneValeurs = {
    cle: string
    /** Début du groupe, tel qu'il s'affiche et se compare. */
    x: string
    /**
     * LES BORNES D'UNE PÉRIODE (V6, §7.10 du plan de rattrapage).
     *
     * Une période de facturation n'a pas une date : elle a un « Du » et un
     * « Au », servis en couple par le lecteur. Les écrire en une seule colonne
     * « Date » ferait passer le début pour toute la période. `null` partout
     * ailleurs : un jour n'a pas de borne de fin à montrer.
     */
    debut: string | null
    fin: string | null
    /**
     * L'HEURE À LAQUELLE LA PMAX A ÉTÉ ATTEINTE (V6), telle que `meta.at` la
     * sert, prise sur le point qui porte le maximum du groupe. `null` quand rien
     * ne la dit : une heure d'atteinte ne se devine pas.
     */
    atteinteA: string | null
    moyenne: number | null
    min: number | null
    max: number | null
    somme: number | null
    /** Valeurs servies dans le groupe. `0` = aucune mesure : la ligne reste vide. */
    n: number
    /**
     * Points RÉELLEMENT agrégés derrière les valeurs de la ligne, tels que le
     * lecteur les compte (`meta.n`). `null` = il ne l'a pas dit ; la couverture
     * reste inconnue plutôt que devinée.
     */
    pointsAgreges: number | null
    /**
     * Ce que la ligne DEVAIT agréger, quand on peut le savoir. `null` sinon : un
     * attendu plausible ferait passer un calcul complet pour une estimation.
     */
    attendus: number | null
    /**
     * Vrai quand le lecteur dit qu'au moins une valeur de la ligne a été agrégée
     * sur moins de points que l'attendu. Une ligne calculée sur deux points ne
     * doit pas ressembler à une ligne pleine.
     */
    partiel: boolean
    /**
     * D'où vient la valeur de CETTE ligne. `null` quand rien n'a été servi pour
     * le dire : une origine ne se devine pas (décision du 11/09).
     */
    origine: OrigineSerie | null
    /**
     * Combien de pas de la ligne n'ont RIEN rendu. Un total calculé sur une
     * fenêtre trouée est plus bas qu'un total complet, et sans ce compte il se
     * lit comme une consommation plus faible (DEV-1508 lot 4, item 32, E2).
     */
    manquants: number
}

/**
 * Les lignes du dépliant, du plus récent au plus ancien (spec v1.5 §2 règle 3).
 *
 * ⚠️ `v[i] === null` n'entre dans aucun calcul et ne compte pas dans `n`. Un
 * groupe entièrement absent rend une ligne à cellules vides : ni 0, ni tiret.
 */
export function lignesDuTableau(params: {
    x: Array<string | [string, string]>
    v: Array<number | null>
    grain: GrainTableau
    /**
     * L'origine de chaque point, alignée sur `x`. Absente = rien n'a été servi
     * pour la dire ; la ligne ne l'invente pas.
     */
    origines?: Array<OrigineSerie | null> | null
    /** Points réellement agrégés par point servi (`meta.n`), aligné sur `x`. */
    n?: Array<number | null> | null
    /** Drapeau `partiel` du lecteur, aligné sur `x`. */
    partiel?: Array<boolean | null> | null
    /** Ce qu'UN point servi devait agréger, quand on le sait. */
    attendu?: number | null
    /** `meta.at` : l'heure d'atteinte de chaque point, alignée sur `x`. */
    heuresAtteinte?: Array<string | null> | null
}): LigneValeurs[] {
    const groupes = new Map<string, number[]>()
    const origines = new Map<string, Array<OrigineSerie | null>>()
    const couverture = new Map<
        string,
        { points: number | null; attendus: number | null; partiel: boolean }
    >()
    /** Pas servis qui n'ont rendu aucune valeur, par groupe (item 32). */
    const manquants = new Map<string, number>()
    /** Bornes d'une période et heure d'atteinte du maximum, par groupe (V6). */
    const bornes = new Map<
        string,
        { debut: string | null; fin: string | null }
    >()
    const atteintes = new Map<
        string,
        { valeur: number; at: string | null } | null
    >()
    const ordre: string[] = []

    params.x.forEach((point, i) => {
        const iso = Array.isArray(point) ? point[0] : point
        if (typeof iso !== "string" || iso === "") return
        const cle = cleDeGroupe(iso, params.grain)
        if (!groupes.has(cle)) {
            groupes.set(cle, [])
            origines.set(cle, [])
            couverture.set(cle, {
                points: null,
                attendus: null,
                partiel: false,
            })
            manquants.set(cle, 0)
            bornes.set(cle, { debut: null, fin: null })
            atteintes.set(cle, null)
            ordre.push(cle)
        }
        // LES BORNES DE LA PÉRIODE : le couple [début, fin] servi par le
        // lecteur. Un groupe qui en porte plusieurs (cas d'école) garde le plus
        // tôt et le plus tard : ce qu'il couvre, et rien de plus.
        if (Array.isArray(point)) {
            const borne = bornes.get(cle)
            if (borne) {
                if (borne.debut === null || point[0] < borne.debut) {
                    borne.debut = point[0]
                }
                // « Au » est le DERNIER JOUR COUVERT : la fin servie est
                // exclusive, et l'écrire telle quelle faisait partager un jour
                // à deux périodes consécutives (lot 5d4c).
                const finPoint = finInclusiveDeLaPeriode(point)
                if (
                    typeof finPoint === "string" &&
                    finPoint !== "" &&
                    (borne.fin === null || finPoint > borne.fin)
                ) {
                    borne.fin = finPoint
                }
            }
        }
        const valeur = params.v[i]
        if (!(typeof valeur === "number" && Number.isFinite(valeur))) {
            // Un pas sans valeur : il ne pèse dans aucun calcul, mais il se
            // COMPTE, pour que le total de la ligne dise sur quoi il porte.
            manquants.set(cle, (manquants.get(cle) ?? 0) + 1)
        }
        if (typeof valeur === "number" && Number.isFinite(valeur)) {
            groupes.get(cle)?.push(valeur)
            // L'origine ne compte que là où une valeur a été servie : un pas
            // absent n'a rien à colorer.
            origines.get(cle)?.push(params.origines?.[i] ?? null)
            // LA COUVERTURE, de la même façon : elle ne se compte que sur les
            // points servis. Un pas absent n'ajoute ni point agrégé ni attendu,
            // sans quoi une ligne à trous se lirait « partielle » deux fois.
            const couvre = couverture.get(cle)
            if (couvre) {
                const points = params.n?.[i]
                if (typeof points === "number" && Number.isFinite(points)) {
                    couvre.points = (couvre.points ?? 0) + points
                }
                if (typeof params.attendu === "number" && params.attendu > 0) {
                    couvre.attendus = (couvre.attendus ?? 0) + params.attendu
                }
                if (params.partiel?.[i] === true) couvre.partiel = true
            }
            // L'HEURE D'ATTEINTE SUIT LE MAXIMUM, pas le dernier point : sur un
            // groupe agrégé, c'est l'heure du point le plus haut qui dit quand
            // la pointe a eu lieu. Le premier maximum l'emporte sur un ex æquo.
            const heure = params.heuresAtteinte?.[i] ?? null
            const meilleur = atteintes.get(cle) ?? null
            if (meilleur === null || valeur > meilleur.valeur) {
                atteintes.set(cle, { valeur, at: heure })
            }
        }
    })

    return ordre
        .map((cle) => {
            const valeurs = groupes.get(cle) ?? []
            const origine = origineDUnGroupe(origines.get(cle) ?? [])
            const couvre = couverture.get(cle) ?? {
                points: null,
                attendus: null,
                partiel: false,
            }
            if (valeurs.length === 0) {
                return {
                    cle,
                    x: cle,
                    debut: bornes.get(cle)?.debut ?? null,
                    fin: bornes.get(cle)?.fin ?? null,
                    atteinteA: null,
                    moyenne: null,
                    min: null,
                    max: null,
                    somme: null,
                    n: 0,
                    pointsAgreges: null,
                    attendus: null,
                    partiel: false,
                    origine: null,
                    manquants: manquants.get(cle) ?? 0,
                }
            }
            const somme = valeurs.reduce((a, b) => a + b, 0)
            return {
                cle,
                x: cle,
                debut: bornes.get(cle)?.debut ?? null,
                fin: bornes.get(cle)?.fin ?? null,
                atteinteA: atteintes.get(cle)?.at ?? null,
                moyenne: somme / valeurs.length,
                min: Math.min(...valeurs),
                max: Math.max(...valeurs),
                somme,
                n: valeurs.length,
                pointsAgreges: couvre.points,
                attendus: couvre.attendus,
                // Le drapeau du lecteur d'abord ; à défaut, un compte inférieur
                // à l'attendu le dit tout aussi bien.
                partiel:
                    couvre.partiel ||
                    (couvre.points !== null &&
                        couvre.attendus !== null &&
                        couvre.points < couvre.attendus),
                origine,
                manquants: manquants.get(cle) ?? 0,
            }
        })
        .sort((a, b) => (a.x < b.x ? 1 : a.x > b.x ? -1 : 0))
}

// ============================================================================
// LES COLONNES ET LA PAGINATION
// ============================================================================

/**
 * Une colonne du tableau. `qualite` n'est pas un champ de la ligne : l'état du
 * jour vient de la BANDE de complétude, qui est la seule à le connaître, et la
 * colonne le lit à la date de la ligne. Les trois colonnes du miroir (lot 5g)
 * n'en sont pas non plus : elles lisent la paire de séries de la figure.
 */
export type ColonneValeurs = {
    cle:
        | keyof LigneValeurs
        | "qualite"
        | "soutiree"
        | "injectee"
        | "qualiteInjection"
    libelle: string
}

/** La colonne qui dit d'où vient la valeur, ajoutée en dernier (lot 2e). */
const COLONNE_ORIGINE: ColonneValeurs = { cle: "origine", libelle: "Origine" }

/**
 * LA COLONNE « POINTS » (lot 3, E2). Combien de points le lecteur a réellement
 * agrégés derrière la ligne, sur combien il en attendait.
 *
 * Sans elle, une moyenne calculée sur deux pas d'une heure a exactement la même
 * tête qu'une moyenne calculée sur les douze : l'écran affirme alors autant dans
 * les deux cas, ce qui est faux dans l'un des deux.
 */
const COLONNE_POINTS: ColonneValeurs = {
    cle: "pointsAgreges",
    libelle: "Points",
}

/**
 * LA COLONNE « QUALITÉ » (V3, spec v1.5 §2 règle 2). L'état du jour, avec les
 * mots et les couleurs de la bande de complétude — pas un second vocabulaire.
 * Elle se pose en dernier, comme dans la maquette.
 */
const COLONNE_QUALITE: ColonneValeurs = { cle: "qualite", libelle: "Qualité" }

/**
 * L'HEURE D'ATTEINTE DE LA PMAX (V6). Elle vit à côté de la valeur qu'elle
 * date, pas en bout de ligne : sans elle, une PMax est un nombre sans instant.
 */
const COLONNE_ATTEINTE: ColonneValeurs = {
    cle: "atteinteA",
    libelle: "Atteinte à",
}

/** Le mot posé sur une ligne agrégée sur moins de points que l'attendu. */
export const MENTION_PARTIEL = "Partiel"

/**
 * Ce qui s'écrit dans la colonne « Points » : les points agrégés, et l'attendu
 * quand on le connaît. Une couverture inconnue laisse la cellule VIDE — elle ne
 * se remplit pas d'un compte de lignes qui ne dit rien de la mesure.
 */
export function texteDesPoints(ligne: LigneValeurs): string {
    if (ligne.pointsAgreges === null) return ""
    return ligne.attendus === null
        ? String(ligne.pointsAgreges)
        : `${ligne.pointsAgreges} / ${ligne.attendus}`
}

/**
 * CE QUI S'ÉCRIT DANS LA CELLULE « TOTAL » (DEV-1508 lot 4, item 32, E2).
 *
 * Le total d'une ligne est la somme des pas SERVIS. Quand la ligne couvre des
 * pas qui n'ont rien rendu, ce total porte sur moins de temps qu'il n'y paraît :
 * écrit nu, il se lit comme une consommation plus faible. Il dit donc combien
 * de pas lui manquent.
 *
 * Trois règles, dans cet ordre :
 *  • une somme absente laisse la cellule VIDE, comme toute absence sur cette
 *    page. Une parenthèse seule dans une cellule vide dirait un total de rien ;
 *  • zéro pas manquant n'écrit RIEN. « (0 pas manquants) » est du bruit ;
 *  • le pluriel suit le compte : « (1 pas manquant) », « (17 pas manquants) ».
 */
export function texteDuTotal(
    ligne: LigneValeurs,
    formate: (valeur: number) => string = formateNombre
): string {
    return texteDeLaSomme(ligne.somme, ligne.manquants, formate)
}

/**
 * LA MÊME RÈGLE, SUR N'IMPORTE QUELLE SOMME (DEV-1508 lot B4).
 *
 * Le total par série posé en tête de la carte du graphe dit exactement ce que
 * dit la cellule « Total » du tableau, avec les mêmes mots et le même pluriel :
 * deux phrases différentes pour le même fait feraient douter de la donnée. La
 * règle vit donc ici une seule fois, et les deux surfaces l'appellent.
 */
export function texteDeLaSomme(
    somme: number | null,
    manquants: number,
    formate: (valeur: number) => string = formateNombre
): string {
    if (somme === null) return ""
    const nombre = formate(somme)
    if (manquants <= 0) return nombre
    const mot = manquants === 1 ? "pas manquant" : "pas manquants"
    return `${nombre} (${manquants} ${mot})`
}

/**
 * LE FORMAT DES NOMBRES DE CETTE PAGE, en un seul endroit (DEV-1508 lot B4).
 *
 * Séparateurs français, deux décimales au plus. Le total par série de la carte
 * du graphe et la colonne « Total » du tableau chiffrent la même chose : les
 * voir écrits avec deux formats différents ferait chercher une différence qui
 * n'existe pas.
 */
export function formateNombre(valeur: number): string {
    return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(
        valeur
    )
}

/**
 * Les colonnes du tableau. Un grain agrégé porte moyenne, min et max ; une
 * grandeur qui s'additionne porte la somme. Au grain servi tel quel, une seule
 * colonne de valeur suffit.
 */
export function colonnesDuTableau(params: {
    agrege: boolean
    /** Vrai pour une grandeur qui s'additionne (énergie, durée). */
    sommable: boolean
    /** Vrai quand l'origine des points est connue : elle prend sa colonne. */
    avecOrigine?: boolean
    /** Vrai quand la couverture est connue : elle prend la sienne. */
    avecPoints?: boolean
    /**
     * Série servie au grain PÉRIODE : la colonne « Date » laisse la place au
     * couple « Du » / « Au » (V6).
     */
    periode?: boolean
    /** Vrai quand `meta.at` date au moins un maximum : « Atteinte à » (V6). */
    avecAtteinte?: boolean
    /** Vrai quand l'état du jour est connu : la colonne « Qualité » (V3). */
    avecQualite?: boolean
}): ColonneValeurs[] {
    const colonnes: ColonneValeurs[] = params.periode
        ? [
              { cle: "debut", libelle: "Du" },
              { cle: "fin", libelle: "Au" },
          ]
        : [{ cle: "x", libelle: "Date" }]
    const finir = () => {
        if (params.avecAtteinte) colonnes.push(COLONNE_ATTEINTE)
        if (params.avecPoints) colonnes.push(COLONNE_POINTS)
        if (params.avecOrigine) colonnes.push(COLONNE_ORIGINE)
        if (params.avecQualite) colonnes.push(COLONNE_QUALITE)
        return colonnes
    }
    if (params.sommable) {
        colonnes.push({ cle: "somme", libelle: "Total" })
        if (params.agrege) {
            colonnes.push(
                { cle: "moyenne", libelle: "Moyenne" },
                { cle: "min", libelle: "Minimum" },
                { cle: "max", libelle: "Maximum" }
            )
        }
        return finir()
    }
    if (!params.agrege) {
        colonnes.push({ cle: "moyenne", libelle: "Valeur" })
        return finir()
    }
    colonnes.push(
        { cle: "moyenne", libelle: "Moyenne" },
        { cle: "min", libelle: "Minimum" },
        { cle: "max", libelle: "Maximum" }
    )
    return finir()
}

/**
 * LES COLONNES DU TABLEAU EN MIROIR (lot 5g, `tableMiroir` de la maquette).
 *
 * Date · Soutirée · Qualité · Injectée · Qualité · Origine. Les deux sens sont
 * CÔTE À CÔTE, chacun avec sa qualité — une date peut être mesurée d'un côté et
 * manquante de l'autre, et une seule colonne de qualité mentirait sur l'un des
 * deux. L'origine est commune : les deux flux d'un même compteur viennent du
 * même relevé.
 *
 * Aucune colonne de différence, de solde ou de net : deux nombres, jamais leur
 * soustraction (E5).
 */
export function colonnesDuMiroir(): ColonneValeurs[] {
    return [
        { cle: "x", libelle: "Date" },
        { cle: "soutiree", libelle: "Soutirée" },
        { cle: "qualite", libelle: "Qualité" },
        { cle: "injectee", libelle: "Injectée" },
        { cle: "qualiteInjection", libelle: "Qualité" },
        COLONNE_ORIGINE,
    ]
}

/**
 * LA CLÉ D'UNE COLONNE ALIGNÉE SUR `x`, dite pareil des deux côtés (V6).
 *
 * `meta.at` est indexé par `variable.stat`, et une même variable existe dans les
 * deux directions. La vue et le dépliant composent donc la même clé, ici, une
 * seule fois : deux compositions écrites à deux endroits finiraient par diverger
 * et le tableau daterait une pointe avec l'heure d'une autre.
 */
export function cleColonneAlignee(serie: {
    direction: string
    variable: string
    stat: string
}): string {
    return `${serie.direction}|${serie.variable}.${serie.stat}`
}

/** Nombre de pages, au moins une (une page vide se dit, elle ne disparaît pas). */
export function nombreDePages(lignes: number, parPage: number): number {
    return Math.max(1, Math.ceil(lignes / Math.max(1, parPage)))
}

/**
 * La page qui porte cette date, ou `null` quand aucune ligne ne la porte : un
 * « aller au » qui tombe dans un trou ne doit pas sauter à une page au hasard.
 */
export function pageDeLaDate(
    lignes: LigneValeurs[],
    date: string,
    parPage: number = LIGNES_PAR_PAGE
): number | null {
    const jour = String(date).slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(jour)) return null
    const index = lignes.findIndex((ligne) => ligne.x.slice(0, 10) === jour)
    if (index < 0) return null
    return Math.floor(index / Math.max(1, parPage))
}

/** Les lignes d'une page (page 0 = la première). */
export function pageDeLignes(
    lignes: LigneValeurs[],
    page: number,
    parPage: number = LIGNES_PAR_PAGE
): LigneValeurs[] {
    const taille = Math.max(1, parPage)
    const debut = Math.max(0, page) * taille
    return lignes.slice(debut, debut + taille)
}
